import { MessageModal } from "../../interfaces/MessageObject";
import {
  ProductDetailsModal,
  AddInfo,
  SellingPoint,
  Query,
} from "../../interfaces/ProductObject";
import {
  convertProductDetailsToAddInfo,
  getProductImages,
  getLikesAndTrust,
} from "../converters/backendToUi/productConvertor";
import genericUtility from "../utility/GenericUtility";
import { BusinessProfile } from "../../interfaces/BusinessObject";
import ApiService from "../apiServices/ProductApiService"; // (not used for now)

class ProductDetailHelper {
  /**
   * Converts a ProductDetailsModal object into an AddInfo object using the converter method.
   * @param productDetails - The ProductDetailsModal object to convert.
   * @returns An AddInfo object.
   */
  public static retrieveProductDetailsInfo(
    productDetails: ProductDetailsModal,
  ): AddInfo {
    return convertProductDetailsToAddInfo(productDetails);
  }

  public static retrieveSellingPoints(
    productDetails: ProductDetailsModal,
  ): SellingPoint[] | undefined {
    return productDetails.sellingPoints;
  }

  public static retrieveImages(productDetails: ProductDetailsModal): string[] {
    return getProductImages(productDetails);
  }

  public static retrieveLikesAndTrust(productDetails: ProductDetailsModal): {
    likesCount: number;
    isTrustedSeller: boolean;
  } {
    return getLikesAndTrust(productDetails);
  }

  public static retrieveQueries(productDetails: ProductDetailsModal): Query[] {
    const directQueries = productDetails.queries as any[] | undefined;
    if (directQueries && directQueries.length > 0) {
      return directQueries as unknown as Query[];
    }
    // Fallback: map backend productQueries → UI Query[]
    const backendProductQueries: any[] | undefined = (productDetails as any)
      ?.productQueries;
    if (!backendProductQueries || backendProductQueries.length === 0) return [];
    return backendProductQueries.map((q: any) => ({
      id: q?.queryId,
      question: q?.queryContent?.question ?? "",
      answer: q?.queryContent?.answer ?? undefined,
      askedBy: q?.buyerId,
      timestamp: q?.createdAt,
    }));
  }

  public static isCurrentUserSeller(
    productDetails: ProductDetailsModal,
  ): boolean {
    // If user is not authenticated (or token expired), they cannot be the buyer / seller
    if (!genericUtility.isUserLoggedIn()) {
      return false;
    }

    const currentUserId = genericUtility.getCookie("userId");

    return productDetails.sellerId === currentUserId;
  }

  /**
   * Determines whether the current user owns or collaborates on the given
   * business. `myBusinesses` already contains only the businesses the current
   * user owns or collaborates on, so membership by businessId is sufficient.
   * Returns false when there is no businessId.
   */
  public static isCurrentUserOwnerOrCollaborator(
    businessId: string | undefined,
    myBusinesses: BusinessProfile[],
  ): boolean {
    // If user is not authenticated (or token expired), they cannot be owner / collaborator
    if (!genericUtility.isUserLoggedIn()) {
      return false;
    }

    if (!businessId) {
      return false;
    }

    return myBusinesses.some((b) => b.businessId === businessId);
  }

  /**
   * Retrieves a ProductDetailsModal object by its ID from the given ProductListDb dictionary.
   * @param productListDb - The dictionary of productId and ProductDetailsModal.
   * @param productId - The ID of the product to retrieve.
   * @returns The corresponding ProductDetailsModal object or a placeholder if not found.
   */
  public static getProductById(
    productListDb: Record<string, ProductDetailsModal>,
    productId: string,
  ): ProductDetailsModal {
    const product = productListDb[productId];
    if (!product) {
      console.error(`Product with ID ${productId} not found.`);
      return { productId: productId } as ProductDetailsModal; // Return a placeholder ProductDetailsModal object
    }

    return product;
  }

  // Deprecated: depends on an API method and message shape we don't expose
  // Keeping stub for future wiring if needed
  public static getProductDetailsByMessage(
    _message: MessageModal,
  ): ProductDetailsModal {
    return { productId: "unknown" } as ProductDetailsModal;
  }

  public static increaseOfferCountInProductDetail(
    productDetails: ProductDetailsModal,
  ): ProductDetailsModal {
    const current = (productDetails.offers?.outStandingOffersCount as any) ?? 0;
    const nextCount = Number(current) + 1;
    return {
      ...productDetails,
      offers: { outStandingOffersCount: nextCount } as any,
    } as ProductDetailsModal;
  }
}

export default ProductDetailHelper;
